/**
 * FP3-043 Phase B — Clean Pilot Org Wizard.
 *
 *   /platform/pilot-setup   (super_admin only)
 *
 * Guided 6-section flow:
 *   1. Organization
 *   2. School / Campus
 *   3. First Org Admin invite (required)
 *   4. Optional first Provider invite
 *   5. Review & create
 *   6. Done — verification summary
 *
 * Backed by:
 *   GET  /api/platform/pilot-setup/precheck?name=...   duplicate-name probe
 *   POST /api/platform/pilot-setup                     atomic-ish create
 *
 * Hard rules
 *   • super_admin only — ProtectedRoute role gate at the router layer +
 *     a defensive in-page redirect if a non-super reaches it directly.
 *   • Reuses existing org / school / invite endpoints under the hood
 *     (no shortcut paths). Backend is the source of truth for scope
 *     and validation; the FE only mirrors the same rules so the user
 *     sees inline feedback before they submit.
 *   • Never displays invite tokens, hashes, or any secret. The
 *     `email_status` pill on the verification screen is the only hint
 *     about whether Resend delivered.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Buildings,
  CheckCircle,
  GraduationCap,
  ShieldCheck,
  Stethoscope,
  Warning,
  WarningCircle,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const ACCOUNT_TYPES = [
  { v: "school_team", l: "School / team" },
  { v: "individual_provider", l: "Solo provider" },
  { v: "district", l: "District" },
  { v: "other", l: "Other" },
];

const ACCESS_TYPES = [
  { v: "pilot", l: "Pilot" },
  { v: "trial", l: "Trial" },
  { v: "limited", l: "Limited" },
  { v: "membership", l: "Membership" },
];

const ADMIN_ROLES = [
  { v: "org_admin", l: "Organization Admin" },
  { v: "admin", l: "Admin" },
];

const PROVIDER_SPECIALTIES = [
  "", "SLP", "SLPA", "OT", "COTA", "PT", "PTA",
  "Special Education Teacher", "School Psychologist", "Behavior Specialist",
  "Counselor", "Reading Specialist", "Interventionist", "Other",
];

const STEPS = [
  { id: "org", label: "Organization", icon: Buildings },
  { id: "school", label: "Campus", icon: GraduationCap },
  { id: "admin", label: "First Admin", icon: ShieldCheck },
  { id: "provider", label: "Provider (optional)", icon: Stethoscope },
  { id: "review", label: "Review", icon: CheckCircle },
];

const EMPTY_FORM = {
  organization: {
    name: "",
    account_type: "school_team",
    access_type: "pilot",
    billing_interval: "none",
    notes: "",
    primary_contact_name: "",
    primary_contact_email: "",
    primary_contact_phone: "",
  },
  school: {
    name: "",
    email_domain: "",
  },
  admin: {
    first_name: "",
    last_name: "",
    email: "",
    permission_role: "org_admin",
    note: "",
  },
  provider_enabled: false,
  provider: {
    first_name: "",
    last_name: "",
    email: "",
    provider_specialty: "",
    note: "",
  },
  send_invite_emails: true,
};

export default function PilotSetup() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0); // 0..STEPS.length
  const [form, setForm] = useState(EMPTY_FORM);
  const [precheck, setPrecheck] = useState({ exists: false, matches: [] });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // ── Duplicate-name precheck — debounced as the user types ───
  useEffect(() => {
    const name = (form.organization.name || "").trim();
    if (!name) {
      setPrecheck({ exists: false, matches: [] });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.pilotSetupPrecheck(name);
        setPrecheck(r.data || { exists: false, matches: [] });
      } catch {
        setPrecheck({ exists: false, matches: [] });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.organization.name]);

  const upd = (path, value) =>
    setForm((f) => {
      const next = { ...f };
      const [section, field] = path.split(".");
      if (field) {
        next[section] = { ...next[section], [field]: value };
      } else {
        next[section] = value;
      }
      return next;
    });

  // ── Per-step validators (mirror backend rules) ──────────────
  const validation = useMemo(() => {
    const errs = {};
    const orgName = form.organization.name.trim();
    if (!orgName) errs.org_name = "Organization name is required.";
    if (precheck.exists) errs.org_name = "An org with this name already exists.";
    const schoolNeeded =
      form.organization.account_type === "school_team" ||
      form.organization.account_type === "district";
    if (schoolNeeded && !form.school.name.trim())
      errs.school_name = "School / campus name is required for this org type.";
    if (!form.admin.first_name.trim()) errs.admin_first_name = "Required";
    if (!form.admin.last_name.trim()) errs.admin_last_name = "Required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.admin.email.trim()))
      errs.admin_email = "Valid email is required.";
    if (form.provider_enabled) {
      if (!form.provider.first_name.trim()) errs.provider_first_name = "Required";
      if (!form.provider.last_name.trim()) errs.provider_last_name = "Required";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.provider.email.trim()))
        errs.provider_email = "Valid email is required.";
    }
    return errs;
  }, [form, precheck.exists]);

  const stepHasErrors = (s) => {
    if (s === 0) return !!validation.org_name;
    if (s === 1) return !!validation.school_name;
    if (s === 2)
      return !!(
        validation.admin_first_name ||
        validation.admin_last_name ||
        validation.admin_email
      );
    if (s === 3 && form.provider_enabled)
      return !!(
        validation.provider_first_name ||
        validation.provider_last_name ||
        validation.provider_email
      );
    return false;
  };

  const canGoNext = !stepHasErrors(step);
  const allValid = Object.keys(validation).length === 0;

  // Defensive in-page gate (kept AFTER all hooks so React's Rules of
  // Hooks aren't violated — ESLint react-hooks/rules-of-hooks fires
  // when an early return precedes useState/useEffect/useMemo, and
  // CRA's dev-server then shows a full-screen overlay that blocks
  // the wizard entirely). ProtectedRoute already enforces auth; this
  // gate exists so a non-super_admin who reaches the URL is bounced
  // home rather than seeing a backend 403.
  if (user && (user.permission_role || "").toLowerCase() !== "super_admin") {
    return <Navigate to="/" replace />;
  }

  const onSubmit = async () => {
    if (!allValid) {
      toast.error("Please resolve the form errors before creating the pilot org.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        organization: form.organization,
        school: form.school,
        admin: form.admin,
        provider: form.provider_enabled ? form.provider : null,
        send_invite_emails: form.send_invite_emails,
      };
      const r = await api.pilotSetupCreate(payload);
      setResult(r.data);
      setStep(STEPS.length); // jump to Done
    } catch (e) {
      // Pydantic 422 returns `detail` as an array of error objects, not a
      // string. Rendering it directly into JSX crashes React with
      // "Objects are not valid as a React child", so normalise here before
      // setError(). String detail → as-is; array → join the messages.
      const d = e?.response?.data?.detail;
      let msg;
      if (typeof d === "string") {
        msg = d;
      } else if (Array.isArray(d)) {
        msg = d
          .map((x) =>
            typeof x === "string" ? x : x?.msg || JSON.stringify(x)
          )
          .join("; ");
      } else {
        msg = "Could not create the pilot org. Check the form and retry.";
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Done screen — show summary + actions ────────────────────
  if (result) {
    return (
      <DoneScreen
        result={result}
        onAnother={() => {
          setForm(EMPTY_FORM);
          setResult(null);
          setStep(0);
        }}
        navigate={navigate}
      />
    );
  }

  // ── Stepper UI ──────────────────────────────────────────────
  return (
    <div
      data-testid="pilot-setup-page"
      className="min-h-screen bg-[#F7F5EF] body-font"
    >
      <div className="mx-auto max-w-4xl px-5 sm:px-8 py-8 sm:py-10">
        <Link
          to="/platform/dashboard"
          data-testid="pilot-setup-back-link"
          className="inline-flex items-center gap-1.5 text-[12px] text-[#6B7280] hover:text-[#1E3F33] mb-4 transition-colors"
        >
          <ArrowLeft size={13} weight="bold" />
          Back to Platform Dashboard
        </Link>

        <header className="mb-7">
          <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#1E3F33] font-semibold mb-1.5">
            Super Admin · Pilot setup
          </div>
          <h1 className="text-[26px] sm:text-[30px] font-semibold text-[#1F2937] heading-font leading-tight">
            Create a clean pilot organization
          </h1>
          <p className="mt-2 text-[13.5px] text-[#4B5563] leading-relaxed max-w-2xl">
            This will create a new pilot organization, one campus, and a
            scoped invite for the first Org Admin. Nothing is created until
            you press <strong>Create pilot org</strong> on the review screen.
          </p>
        </header>

        <Stepper step={step} />

        <Card
          data-testid="pilot-setup-card"
          className="mt-6 border-[#E5E3DB] shadow-[0_2px_14px_rgba(31,41,55,0.04)] rounded-2xl"
        >
          <CardContent className="p-6 sm:p-8">
            {step === 0 && (
              <OrgStep
                form={form}
                upd={upd}
                precheck={precheck}
                err={validation}
              />
            )}
            {step === 1 && (
              <SchoolStep form={form} upd={upd} err={validation} />
            )}
            {step === 2 && (
              <AdminStep form={form} upd={upd} err={validation} />
            )}
            {step === 3 && (
              <ProviderStep form={form} upd={upd} err={validation} />
            )}
            {step === 4 && (
              <ReviewStep form={form} err={validation} />
            )}

            {error && (
              <div
                data-testid="pilot-setup-error"
                className="mt-5 text-[12.5px] text-[#7A3A2C] bg-[#F8EFEA] border border-[#E8D6CD] rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <div className="mt-7 pt-5 border-t border-[#F1F2F4] flex items-center justify-between">
              <Button
                data-testid="pilot-setup-prev-btn"
                variant="outline"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0 || submitting}
                className="h-9 px-3 text-[12.5px] border-[#E5E3DB] text-[#1F2937] hover:bg-[#F8F7F2]"
              >
                <ArrowLeft size={13} weight="bold" /> Back
              </Button>
              {step < STEPS.length - 1 ? (
                <Button
                  data-testid="pilot-setup-next-btn"
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canGoNext}
                  className="h-9 px-4 text-[12.5px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/40 text-white"
                >
                  Next <ArrowRight size={13} weight="bold" />
                </Button>
              ) : (
                <Button
                  data-testid="pilot-setup-submit-btn"
                  onClick={onSubmit}
                  disabled={submitting || !allValid}
                  className="h-10 px-5 text-[13px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/40 text-white"
                >
                  {submitting ? "Creating…" : "Create pilot org"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Stepper ─────────────────────────────────────────────────────────

function Stepper({ step }) {
  return (
    <ol
      data-testid="pilot-setup-stepper"
      className="flex flex-wrap gap-2 text-[11.5px]"
    >
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const isDone = i < step;
        const isCurrent = i === step;
        return (
          <li
            key={s.id}
            data-testid={`pilot-setup-step-${s.id}${isCurrent ? "-current" : ""}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border ${
              isCurrent
                ? "border-[#1E3F33] bg-[#1E3F33] text-white"
                : isDone
                  ? "border-[#8FA998] bg-[#E8F0EB] text-[#1E3F33]"
                  : "border-[#E5E3DB] bg-white text-[#6B7280]"
            }`}
          >
            <Icon size={12} weight="duotone" />
            <span className="font-semibold">{i + 1}. {s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

// ── Step components ─────────────────────────────────────────────────

function FieldRow({ label, required, hint, error, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-[11px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
        {label}
        {required && <span className="text-[#9C6B2E] ml-0.5">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {hint && !error && (
        <p className="text-[11px] text-[#6B7280] mt-1 leading-snug">{hint}</p>
      )}
      {error && (
        <p
          data-testid={`${testId}-error`}
          className="text-[11px] text-[#A8453A] mt-1"
        >
          {error}
        </p>
      )}
    </div>
  );
}

function OrgStep({ form, upd, precheck, err }) {
  return (
    <div className="space-y-5">
      <div>
        <div className="text-[12px] uppercase tracking-[0.12em] text-[#1E3F33] font-semibold">
          Step 1 of 5 — Organization
        </div>
        <h2 className="text-[18px] font-semibold heading-font text-[#1F2937] mt-1">
          Tell us about the new pilot org
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldRow
          testId="pilot-org-name-row"
          label="Organization name"
          required
          error={err.org_name}
          hint={
            precheck.exists
              ? `Conflicts with: ${precheck.matches.map((m) => m.name).join(", ")}`
              : "We'll create a clean org with no inherited data."
          }
        >
          <Input
            data-testid="pilot-org-name"
            value={form.organization.name}
            onChange={(e) => upd("organization.name", e.target.value)}
            className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
            placeholder="e.g. Lincoln Pilot ISD"
          />
        </FieldRow>
        <FieldRow
          testId="pilot-org-account-type-row"
          label="Organization type"
          required
        >
          <select
            data-testid="pilot-org-account-type"
            value={form.organization.account_type}
            onChange={(e) => upd("organization.account_type", e.target.value)}
            className="block w-full h-10 border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33]"
          >
            {ACCOUNT_TYPES.map((t) => (
              <option key={t.v} value={t.v}>{t.l}</option>
            ))}
          </select>
        </FieldRow>
        <FieldRow label="Access type" testId="pilot-org-access-type-row">
          <select
            data-testid="pilot-org-access-type"
            value={form.organization.access_type}
            onChange={(e) => upd("organization.access_type", e.target.value)}
            className="block w-full h-10 border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33]"
          >
            {ACCESS_TYPES.map((t) => (
              <option key={t.v} value={t.v}>{t.l}</option>
            ))}
          </select>
        </FieldRow>
        <FieldRow label="Billing interval" testId="pilot-org-billing-row">
          <select
            data-testid="pilot-org-billing"
            value={form.organization.billing_interval}
            onChange={(e) => upd("organization.billing_interval", e.target.value)}
            className="block w-full h-10 border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33]"
          >
            <option value="none">None</option>
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
        </FieldRow>
        <FieldRow label="Primary contact name · optional" testId="pilot-org-contact-name-row">
          <Input
            data-testid="pilot-org-contact-name"
            value={form.organization.primary_contact_name}
            onChange={(e) => upd("organization.primary_contact_name", e.target.value)}
            className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
          />
        </FieldRow>
        <FieldRow label="Primary contact email · optional" testId="pilot-org-contact-email-row">
          <Input
            data-testid="pilot-org-contact-email"
            type="email"
            value={form.organization.primary_contact_email}
            onChange={(e) => upd("organization.primary_contact_email", e.target.value)}
            className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
          />
        </FieldRow>
        <div className="sm:col-span-2">
          <FieldRow label="Internal note · optional" hint="Visible to Super Admin only." testId="pilot-org-notes-row">
            <Textarea
              data-testid="pilot-org-notes"
              value={form.organization.notes}
              onChange={(e) => upd("organization.notes", e.target.value)}
              className="min-h-[60px] border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33] resize-y"
            />
          </FieldRow>
        </div>
      </div>
    </div>
  );
}

function SchoolStep({ form, upd, err }) {
  const isSolo = form.organization.account_type === "individual_provider";
  const isRequired =
    form.organization.account_type === "school_team" ||
    form.organization.account_type === "district";
  return (
    <div className="space-y-5">
      <div>
        <div className="text-[12px] uppercase tracking-[0.12em] text-[#1E3F33] font-semibold">
          Step 2 of 5 — Campus
        </div>
        <h2 className="text-[18px] font-semibold heading-font text-[#1F2937] mt-1">
          Add the first campus or workspace
        </h2>
        {isSolo && (
          <p className="mt-2 text-[12.5px] text-[#6B7280] leading-relaxed">
            Solo provider orgs use a single workspace.{" "}
            <button
              type="button"
              data-testid="pilot-school-default-btn"
              onClick={() => upd("school.name", "Primary Workspace")}
              className="text-[#1E3F33] font-medium underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33]"
            >
              Use "Primary Workspace"
            </button>
            .
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldRow
          testId="pilot-school-name-row"
          label={isRequired ? "Campus name" : "Campus name · optional"}
          required={isRequired}
          error={err.school_name}
          hint={
            isRequired
              ? "Required for school / district orgs."
              : "Defaults to 'Primary Workspace' if left blank."
          }
        >
          <Input
            data-testid="pilot-school-name"
            value={form.school.name}
            onChange={(e) => upd("school.name", e.target.value)}
            className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
            placeholder={isSolo ? "Primary Workspace" : "e.g. Lincoln Elementary"}
          />
        </FieldRow>
        <FieldRow
          label="Email domain · optional"
          hint="Used to auto-match invite recipients to this campus."
          testId="pilot-school-domain-row"
        >
          <Input
            data-testid="pilot-school-domain"
            value={form.school.email_domain}
            onChange={(e) => upd("school.email_domain", e.target.value)}
            className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
            placeholder="lincoln.k12.example"
          />
        </FieldRow>
      </div>
    </div>
  );
}

function AdminStep({ form, upd, err }) {
  return (
    <div className="space-y-5">
      <div>
        <div className="text-[12px] uppercase tracking-[0.12em] text-[#1E3F33] font-semibold">
          Step 3 of 5 — First Org Admin
        </div>
        <h2 className="text-[18px] font-semibold heading-font text-[#1F2937] mt-1">
          Invite the person who will run this org
        </h2>
        <p className="mt-2 text-[12.5px] text-[#6B7280] leading-relaxed">
          We'll send them an invite scoped to this organization only. They'll
          accept Terms of Use on first login.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldRow label="First name" required error={err.admin_first_name} testId="pilot-admin-first-row">
          <Input
            data-testid="pilot-admin-first"
            value={form.admin.first_name}
            onChange={(e) => upd("admin.first_name", e.target.value)}
            className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
          />
        </FieldRow>
        <FieldRow label="Last name" required error={err.admin_last_name} testId="pilot-admin-last-row">
          <Input
            data-testid="pilot-admin-last"
            value={form.admin.last_name}
            onChange={(e) => upd("admin.last_name", e.target.value)}
            className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
          />
        </FieldRow>
        <FieldRow label="Email" required error={err.admin_email} testId="pilot-admin-email-row">
          <Input
            data-testid="pilot-admin-email"
            type="email"
            value={form.admin.email}
            onChange={(e) => upd("admin.email", e.target.value)}
            className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
            placeholder="admin@example.org"
          />
        </FieldRow>
        <FieldRow label="Permission role" testId="pilot-admin-role-row">
          <select
            data-testid="pilot-admin-role"
            value={form.admin.permission_role}
            onChange={(e) => upd("admin.permission_role", e.target.value)}
            className="block w-full h-10 border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33]"
          >
            {ADMIN_ROLES.map((r) => (
              <option key={r.v} value={r.v}>{r.l}</option>
            ))}
          </select>
        </FieldRow>
        <div className="sm:col-span-2">
          <FieldRow label="Internal note · optional" hint="Stored with the invite — not shown to the invitee." testId="pilot-admin-note-row">
            <Textarea
              data-testid="pilot-admin-note"
              value={form.admin.note}
              onChange={(e) => upd("admin.note", e.target.value)}
              className="min-h-[60px] border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33] resize-y"
            />
          </FieldRow>
        </div>
        <div className="sm:col-span-2 flex items-start gap-2 pt-2">
          <input
            type="checkbox"
            id="pilot-send-email"
            data-testid="pilot-send-email"
            checked={form.send_invite_emails}
            onChange={(e) => upd("send_invite_emails", e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-[#1E3F33] text-[#1E3F33] focus:ring-[#1E3F33]/30"
          />
          <label
            htmlFor="pilot-send-email"
            className="text-[12.5px] text-[#1F2937] leading-snug"
          >
            Send invite emails. If unchecked, invites are created but no
            emails go out — useful for testing or for orgs you'll hand the
            link to manually from Access & Invites.
          </label>
        </div>
      </div>
    </div>
  );
}

function ProviderStep({ form, upd, err }) {
  return (
    <div className="space-y-5">
      <div>
        <div className="text-[12px] uppercase tracking-[0.12em] text-[#1E3F33] font-semibold">
          Step 4 of 5 — First Provider · optional
        </div>
        <h2 className="text-[18px] font-semibold heading-font text-[#1F2937] mt-1">
          Want to seed the first provider too?
        </h2>
        <p className="mt-2 text-[12.5px] text-[#6B7280] leading-relaxed">
          Optional. Skip this if the Org Admin will invite providers themselves.
        </p>
      </div>
      <div className="flex items-start gap-2 pb-2">
        <input
          type="checkbox"
          id="pilot-provider-enable"
          data-testid="pilot-provider-enable"
          checked={form.provider_enabled}
          onChange={(e) => upd("provider_enabled", e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-[#1E3F33] text-[#1E3F33] focus:ring-[#1E3F33]/30"
        />
        <label
          htmlFor="pilot-provider-enable"
          className="text-[12.5px] text-[#1F2937] leading-snug font-medium"
        >
          Yes — invite a first provider now.
        </label>
      </div>
      {form.provider_enabled && (
        <div
          data-testid="pilot-provider-form"
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          <FieldRow label="First name" required error={err.provider_first_name} testId="pilot-provider-first-row">
            <Input
              data-testid="pilot-provider-first"
              value={form.provider.first_name}
              onChange={(e) => upd("provider.first_name", e.target.value)}
              className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
            />
          </FieldRow>
          <FieldRow label="Last name" required error={err.provider_last_name} testId="pilot-provider-last-row">
            <Input
              data-testid="pilot-provider-last"
              value={form.provider.last_name}
              onChange={(e) => upd("provider.last_name", e.target.value)}
              className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
            />
          </FieldRow>
          <FieldRow label="Email" required error={err.provider_email} testId="pilot-provider-email-row">
            <Input
              data-testid="pilot-provider-email"
              type="email"
              value={form.provider.email}
              onChange={(e) => upd("provider.email", e.target.value)}
              className="h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
            />
          </FieldRow>
          <FieldRow label="Provider specialty" testId="pilot-provider-spec-row">
            <select
              data-testid="pilot-provider-spec"
              value={form.provider.provider_specialty}
              onChange={(e) => upd("provider.provider_specialty", e.target.value)}
              className="block w-full h-10 border border-[#E5E3DB] rounded-md px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3F33]/30 focus:border-[#1E3F33]"
            >
              {PROVIDER_SPECIALTIES.map((s) => (
                <option key={s || "none"} value={s}>{s || "—"}</option>
              ))}
            </select>
          </FieldRow>
        </div>
      )}
    </div>
  );
}

function ReviewStep({ form, err }) {
  const items = [
    { l: "Organization name", v: form.organization.name },
    {
      l: "Organization type",
      v: ACCOUNT_TYPES.find((t) => t.v === form.organization.account_type)?.l,
    },
    {
      l: "Access type",
      v: ACCESS_TYPES.find((t) => t.v === form.organization.access_type)?.l,
    },
    { l: "Billing interval", v: form.organization.billing_interval },
    {
      l: "Campus",
      v:
        form.school.name.trim() ||
        (form.organization.account_type === "individual_provider"
          ? "Primary Workspace (default)"
          : "—"),
    },
    {
      l: "First Org Admin",
      v: `${form.admin.first_name} ${form.admin.last_name} · ${form.admin.email}`,
    },
    {
      l: "Provider invite",
      v: form.provider_enabled
        ? `${form.provider.first_name} ${form.provider.last_name} · ${form.provider.email}${form.provider.provider_specialty ? " · " + form.provider.provider_specialty : ""}`
        : "Skipped",
    },
    {
      l: "Send invite emails",
      v: form.send_invite_emails ? "Yes" : "No",
    },
  ];
  const blocking = Object.values(err).filter(Boolean);

  return (
    <div className="space-y-5">
      <div>
        <div className="text-[12px] uppercase tracking-[0.12em] text-[#1E3F33] font-semibold">
          Step 5 of 5 — Review &amp; create
        </div>
        <h2 className="text-[18px] font-semibold heading-font text-[#1F2937] mt-1">
          Confirm before we create the pilot
        </h2>
        <p className="mt-2 text-[12.5px] text-[#6B7280] leading-relaxed">
          Pressing <strong>Create pilot org</strong> creates the organization,
          campus, and invite(s). Nothing happens until you press that button.
        </p>
      </div>
      <dl
        data-testid="pilot-review"
        className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 border border-[#E5E3DB] rounded-xl bg-[#FBFAF6] p-5"
      >
        {items.map((row) => (
          <div key={row.l} className="flex items-start gap-3">
            <dt className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold w-32 flex-shrink-0">
              {row.l}
            </dt>
            <dd className="text-[12.5px] text-[#1F2937] min-w-0 flex-1 break-words">
              {row.v || "—"}
            </dd>
          </div>
        ))}
      </dl>
      {blocking.length > 0 && (
        <div
          data-testid="pilot-review-warnings"
          className="text-[12px] text-[#7A3A2C] bg-[#F8EFEA] border border-[#E8D6CD] rounded-md px-3 py-2 leading-relaxed"
        >
          <div className="flex items-start gap-2">
            <Warning size={14} weight="duotone" className="mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold mb-0.5">Resolve before creating:</div>
              <ul className="list-disc list-inside space-y-0.5">
                {blocking.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Done screen ─────────────────────────────────────────────────────

function DoneScreen({ result, onAnother, navigate }) {
  const r = result?.readiness || {};
  const adminInvite = result?.admin_invite || {};
  const providerInvite = result?.provider_invite || null;
  const providerError = result?.provider_error || null;
  const sendFailedAdmin =
    (adminInvite.email_status || "").toLowerCase().startsWith("fail");
  const sendFailedProvider =
    providerInvite &&
    (providerInvite.email_status || "").toLowerCase().startsWith("fail");

  return (
    <div className="min-h-screen bg-[#F7F5EF] body-font">
      <div className="mx-auto max-w-3xl px-5 sm:px-8 py-10 sm:py-14">
        <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.14em] text-[#1E3F33] bg-[#E8F0EB] rounded-full px-2.5 py-1 mb-4 font-semibold">
          <CheckCircle size={12} weight="duotone" />
          Pilot org created
        </div>
        <h1
          data-testid="pilot-done-title"
          className="text-[26px] sm:text-[30px] font-semibold heading-font text-[#1F2937] leading-tight"
        >
          {result?.organization?.name || "Pilot organization"} is ready.
        </h1>
        <p className="mt-2 text-[13.5px] text-[#4B5563] leading-relaxed">
          We created the org, campus, and invite(s) — all scoped to the new
          organization. The first admin can sign in as soon as they accept the
          invite and the Terms of Use.
        </p>

        <Card
          data-testid="pilot-done-summary"
          className="mt-7 border-[#E5E3DB] shadow-[0_2px_14px_rgba(31,41,55,0.04)] rounded-2xl"
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-[14px] font-semibold heading-font text-[#1F2937] flex items-center gap-2">
              <ShieldCheck size={16} weight="duotone" className="text-[#1E3F33]" />
              Clean-org readiness
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-6">
              <Stat l="Students" v={r.students} />
              <Stat l="Providers" v={r.providers} />
              <Stat l="Campuses" v={r.schools} />
              <Stat l="Pending invites" v={r.pending_invites} />
              <Stat l="Access requests" v={r.access_requests} />
              <Stat l="Demo data visible" v={r.demo_data_visible ? "Yes" : "No"} tone="ok" />
            </dl>
          </CardContent>
        </Card>

        <Card className="mt-5 border-[#E5E3DB] rounded-2xl">
          <CardContent className="p-5">
            <div className="text-[11px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold mb-2">
              Invites
            </div>
            <ul className="space-y-2.5">
              <li
                data-testid="pilot-done-admin-invite"
                className="flex items-start justify-between gap-3 py-1.5 border-b border-[#F1F2F4] last:border-0"
              >
                <div className="min-w-0">
                  <div className="text-[13px] text-[#1F2937] font-medium">
                    {adminInvite.first_name} {adminInvite.last_name}
                  </div>
                  <div className="text-[11.5px] text-[#6B7280]">
                    {adminInvite.email} · {(adminInvite.intended_role || "").replace("_", " ")}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  {sendFailedAdmin ? (
                    <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-[0.08em] bg-[#FBF3E4] text-[#9C6B2E]">
                      <WarningCircle size={11} weight="duotone" />
                      Email failed
                    </span>
                  ) : (
                    <span className="inline-flex text-[10.5px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-[0.08em] bg-[#E8F0EB] text-[#1E3F33]">
                      {adminInvite.status || "pending"}
                    </span>
                  )}
                </div>
              </li>
              {providerInvite && (
                <li
                  data-testid="pilot-done-provider-invite"
                  className="flex items-start justify-between gap-3 py-1.5"
                >
                  <div className="min-w-0">
                    <div className="text-[13px] text-[#1F2937] font-medium">
                      {providerInvite.first_name} {providerInvite.last_name}
                    </div>
                    <div className="text-[11.5px] text-[#6B7280]">
                      {providerInvite.email} · provider
                      {providerInvite.provider_specialty
                        ? ` · ${providerInvite.provider_specialty}`
                        : ""}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {sendFailedProvider ? (
                      <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-[0.08em] bg-[#FBF3E4] text-[#9C6B2E]">
                        <WarningCircle size={11} weight="duotone" />
                        Email failed
                      </span>
                    ) : (
                      <span className="inline-flex text-[10.5px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-[0.08em] bg-[#E8F0EB] text-[#1E3F33]">
                        {providerInvite.status || "pending"}
                      </span>
                    )}
                  </div>
                </li>
              )}
            </ul>
            {providerError && (
              <div
                data-testid="pilot-done-provider-error"
                className="mt-3 text-[12px] text-[#7A3A2C] bg-[#F8EFEA] border border-[#E8D6CD] rounded-md px-3 py-2"
              >
                Provider invite skipped: {providerError}. You can invite the
                provider later from Access &amp; Invites.
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-7 flex flex-wrap gap-3">
          <Button
            data-testid="pilot-done-open-dashboard"
            onClick={() => navigate("/admin/dashboard")}
            className="h-10 px-4 text-[12.5px] bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            Open Org Dashboard
          </Button>
          <Button
            data-testid="pilot-done-manage-access"
            variant="outline"
            onClick={() => navigate("/admin/access")}
            className="h-10 px-4 text-[12.5px] border-[#E5E3DB] text-[#1F2937] hover:bg-[#F8F7F2]"
          >
            Manage Access &amp; Invites
          </Button>
          <Button
            data-testid="pilot-done-schools"
            variant="outline"
            onClick={() => navigate("/admin/schools")}
            className="h-10 px-4 text-[12.5px] border-[#E5E3DB] text-[#1F2937] hover:bg-[#F8F7F2]"
          >
            Open Schools &amp; Campuses
          </Button>
          <Button
            data-testid="pilot-done-platform"
            variant="outline"
            onClick={() => navigate("/platform/dashboard")}
            className="h-10 px-4 text-[12.5px] border-[#E5E3DB] text-[#1F2937] hover:bg-[#F8F7F2]"
          >
            Back to Platform Dashboard
          </Button>
          <Button
            data-testid="pilot-done-another"
            variant="outline"
            onClick={onAnother}
            className="h-10 px-4 text-[12.5px] border-[#1E3F33] text-[#1E3F33] hover:bg-[#E8F0EB]"
          >
            Create another pilot org
          </Button>
        </div>
      </div>
    </div>
  );
}

function Stat({ l, v, tone }) {
  const colour = tone === "ok" ? "text-[#1E3F33] bg-[#E8F0EB]" : "text-[#1F2937] bg-[#FBFAF6]";
  return (
    <div className={`rounded-lg ${colour} px-3 py-2 border border-[#EDEAE0]`}>
      <div className="text-[9.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold">
        {l}
      </div>
      <div className="text-[16px] font-semibold heading-font mt-0.5">
        {v ?? "—"}
      </div>
    </div>
  );
}
